import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';
import * as haptic from '@theme/haptic';
import { FILTERS_CHANGE_EVENT, carryNostoParams, serverSearch } from '@theme/nosto-facets';

/**
 * @typedef {object} FilterDrawerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} [trigger]
 * @property {HTMLElement} [results]
 */

/**
 * @extends {DialogComponent}
 */
class FilterDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'trigger', 'applyButton'];

  #pendingCount = 0;

  /**
   * Query string this drawer was last rendered from, Nosto facet parameters excluded. A history
   * entry that only differs by those describes the very same server-rendered page.
   */
  #serverSearch = serverSearch(window.location.search);

  connectedCallback() {
    super.connectedCallback();
    this.#registerEvents();
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregisterEvents();
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  /**
   * The control the drawer was opened from. The quick-filter buttons open the very same drawer as
   * the main trigger, so state and focus have to come back to whichever one was used — not always
   * to the main one.
   * @type {HTMLElement | null}
   */
  #opener = null;

  #handleDialogOpen = () => {
    this.#activeTrigger?.setAttribute('aria-expanded', 'true');
  };

  #handleDialogClose = () => {
    const trigger = this.#activeTrigger;
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
    this.#opener = null;
  };

  /** @returns {HTMLElement | null} */
  get #activeTrigger() {
    return this.#opener ?? this.#trigger;
  }

  /** @type {(event: Event) => void} */
  #onFormChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    // Nosto's facets sit in the same form for layout, but they are not Shopify facets: they carry no
    // `name`, no `url_to_add`, and their own controller answers for them.
    if (target.closest('[data-nosto-facets]')) return;

    if (target.type !== 'checkbox' && target.type !== 'radio' && target.type !== 'number') {
      return;
    }

    haptic.light();

    if (target.type === 'checkbox' || target.type === 'radio') {
      const href = target.checked ? target.dataset.urlToAdd : target.dataset.urlToRemove;

      if (href) {
        this.#renderFromURL(new URL(href, window.location.origin));
        return;
      }
    }

    const form = this.refs.form;
    if (!(form instanceof HTMLFormElement)) return;

    this.#submitForm(form);
  };

  /** @type {(event: Event) => void} */
  #onFormSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    this.#submitForm(form);
  };

  /**
   * A Nosto facet change always lands on page 1, so it drops `page` from the URL — and the section
   * still shows the page the server rendered, with its pagination, its counters and its grid one
   * page behind. Everything else about the selection lives outside Liquid, so this is the only
   * change worth a re-render.
   */
  #onFiltersChange = () => {
    const next = serverSearch(window.location.search);
    if (next === this.#serverSearch) return;

    this.#renderFromURL(new URL(window.location.href), { updateHistory: false });
  };

  #onPopState = () => {
    const next = serverSearch(window.location.search);
    // Moving between two Nosto facet states: the server would hand back a byte-identical page, and
    // morphing it in mid-run would wipe the facets and the grid the listing is busy applying. The
    // listing hears the same `popstate` and re-runs on its own.
    if (next === this.#serverSearch) return;

    this.#renderFromURL(new URL(window.location.href), { updateHistory: false });
  };

  /**
   * @param {{ href?: string, reopen?: boolean }} data
   * @param {Event} [event]
   */
  handleNavigate(data, event) {
    event?.preventDefault();
    if (!data || !data.href) return;
    const reopen = typeof data.reopen === 'boolean' ? data.reopen : undefined;
    this.#renderFromURL(new URL(data.href, window.location.origin), { reopen });
  }

  /**
   * @param {Event} [event]
   */
  applyAndClose(event) {
    event?.preventDefault();
    haptic.confirm();

    // Nothing to apply: every control in the drawer already did it on `change` — a facet through its
    // `url_to_add`, the sort and the price through a submit, a Nosto facet through its own
    // controller. Submitting here would ask the server for the page it just rendered, and that
    // re-render brings back the server-side loading state of the listing for no answer to lift it.
    this.closeDialog();
  }

  /**
   * @param {{ filter?: string }} [data] - Optional facet param_name whose accordion should be revealed.
   */
  /**
   * Declarative handlers are called with the event last, and with the parsed `?…` payload before it
   * when the attribute carries one — the main trigger has none, the quick-filter buttons do.
   * @param {...(unknown)} args
   */
  openDrawer = (...args) => {
    const event = args.at(-1);
    const data = args.length > 1 ? args[0] : null;

    const target = event instanceof Event ? event.target : null;
    const opener = target instanceof Element ? target.closest('button') : null;
    this.#opener = opener instanceof HTMLElement ? opener : null;

    this.showDialog();

    const filter = /** @type {{ filter?: unknown } | null} */ (data)?.filter;
    if (typeof filter === 'string') {
      this.#revealFilter(filter);
    }
  };

  closeDrawer = () => {
    this.closeDialog();
  };

  /**
   * Opens the accordion of the targeted facet (the shared `name` attribute
   * closes the others) and scrolls it into view.
   * @param {string} target - A Shopify `param_name`, or a Nosto field when the facet controller has
   * rewritten the button it came from.
   */
  #revealFilter(target) {
    const details = this.#facetAccordion(target);
    if (!(details instanceof HTMLDetailsElement)) return;

    details.open = true;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => {
      details.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /**
   * The accordion a quick-filter button points at.
   * @param {string} target
   * @returns {HTMLDetailsElement | null}
   */
  #facetAccordion(target) {
    const nosto = this.refs.dialog.querySelector(`details[data-nosto-field="${CSS.escape(target)}"]`);
    if (nosto instanceof HTMLDetailsElement) return nosto;

    const native = this.refs.dialog.querySelector(`details[data-filter-param="${CSS.escape(target)}"]`);
    if (!(native instanceof HTMLDetailsElement)) return null;

    // A button the facet controller has not rewritten yet (Nosto still answering) names a Shopify
    // facet, whose accordion is hidden once Nosto owns the drawer. Fall back on the Nosto facet
    // standing in for it, matched on the label the shopper actually read on the button.
    return this.hasAttribute('data-nosto-mode') ? this.#nostoCounterpart(native) : native;
  }

  /**
   * The Nosto facet matching a Shopify one, by label. Nosto names a facet after its indexed
   * attribute, so the Shopify label is a prefix of it ("Taille" for
   * "taille/dimension/contenance (menu déroulant)"), never the other way round.
   * @param {HTMLDetailsElement} native
   * @returns {HTMLDetailsElement | null}
   */
  #nostoCounterpart(native) {
    const label = native.querySelector('summary span span')?.textContent?.trim().toLowerCase();
    if (!label) return null;

    for (const details of this.refs.dialog.querySelectorAll('[data-nosto-facets] details[data-nosto-field]')) {
      const name = details.querySelector('[data-nosto-slot="name"]')?.textContent?.trim().toLowerCase() ?? '';
      if (name.startsWith(label) || label.startsWith(name)) {
        return details instanceof HTMLDetailsElement ? details : null;
      }
    }

    return null;
  }

  #registerEvents() {
    const form = this.refs.form;
    if (form instanceof HTMLFormElement) {
      form.addEventListener('change', this.#onFormChange);
      form.addEventListener('submit', this.#onFormSubmit);
    }
    window.addEventListener('popstate', this.#onPopState);
    document.addEventListener(FILTERS_CHANGE_EVENT, this.#onFiltersChange);
  }

  #unregisterEvents() {
    const form = this.refs.form;
    if (form instanceof HTMLFormElement) {
      form.removeEventListener('change', this.#onFormChange);
      form.removeEventListener('submit', this.#onFormSubmit);
    }
    window.removeEventListener('popstate', this.#onPopState);
    document.removeEventListener(FILTERS_CHANGE_EVENT, this.#onFiltersChange);
  }

  /**
   * @param {HTMLFormElement} form
   */
  async #submitForm(form) {
    if (!(form instanceof HTMLFormElement)) return;
    const params = new URLSearchParams();
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      if (key === 'page') continue;
      if (typeof value !== 'string') continue;
      if (value.trim() === '') continue;
      params.append(key, value);
    }

    // The Nosto facet selection is not in the form — it lives in the URL — so a sort change would
    // silently drop it if it were not carried over.
    carryNostoParams(params, window.location.search);

    const url = new URL(window.location.href);
    url.search = params.toString();
    await this.#renderFromURL(url);
  }

  /**
   * @param {URL} url
   * @param {{ updateHistory?: boolean, reopen?: boolean }} [options]
   */
  async #renderFromURL(url, { updateHistory = true, reopen } = {}) {
    if (!this.sectionId) return;

    const reopenDrawer = typeof reopen === 'boolean' ? reopen : this.refs.dialog.open;

    // A transition only earns its keep when the grid is what the shopper is looking at. While the
    // drawer stays open — every checkbox tick re-renders — the grid sits behind it, so animating it
    // would spend frames on something nobody sees. Leaving the drawer (apply, reset) is the one
    // moment the grid is revealed, and that is exactly when `reopenDrawer` is false.
    const viewTransition = !reopenDrawer;

    this.#serverSearch = serverSearch(url.search);
    this.#pendingCount++;
    this.#setLoading(true);

    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: 'hydration',
        url,
        viewTransition,
      });

      const cleanUrl = new URL(url);
      cleanUrl.searchParams.delete('section_id');

      if (updateHistory) {
        window.history.pushState({}, '', cleanUrl);
      }

      if (reopenDrawer) {
        requestAnimationFrame(() => {
          if (!this.refs.dialog.open) {
            this.showDialog({ immediate: true });
          }
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      this.#pendingCount--;
      if (this.#pendingCount === 0) {
        this.#setLoading(false);
      }
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const isBusy = Boolean(isLoading);
    this.toggleAttribute('data-loading', isBusy);

    const applyButton = this.refs.applyButton;
    if (applyButton instanceof HTMLButtonElement) {
      applyButton.setAttribute('aria-busy', String(isBusy));
    }
  }

  get #trigger() {
    return this.refs.trigger instanceof HTMLButtonElement ? this.refs.trigger : null;
  }
}

if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}
